import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";

// --- 1. Define Custom Styles to Match Screenshot ---
const BORDER_COLOR = "#000000";
const BORDER_STYLE = "solid";

const styles = StyleSheet.create({
  // --- General Page & Container Styles ---
  page: {
    padding: 15,
    fontSize: 8,
    fontFamily: "Helvetica",
  },
  sectionContainer: {
    borderWidth: 1,
    borderColor: BORDER_COLOR,
    borderStyle: BORDER_STYLE,
    marginBottom: 10,
  },
  // --- Header Styles ---
  mainHeader: {
    fontSize: 12,
    paddingVertical: 4,
    textAlign: "center",
    backgroundColor: "#EAEAEA",
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR,
  },
  infoRow: {
    flexDirection: "row",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  infoLabel: {
    width: "40%",
    fontWeight: "bold",
  },
  infoSeparator: {
    width: "5%",
    textAlign: "center",
  },
  infoValue: {
    width: "55%",
  },

  // --- Section Header Bar (VAT RETURNS PERIOD, NET VAT DUE, etc.) ---
  barHeader: {
    fontSize: 10,
    paddingVertical: 5,
    textAlign: "center",
    backgroundColor: "#DEDEDE",
    fontWeight: "bold",
    borderBottomWidth: 1,
    borderTopWidth: 1,
    borderColor: BORDER_COLOR,
  },

  // --- Table Styles ---
  tableContainer: {
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR,
  },
  tableHeaderRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR,
    backgroundColor: "#F0F0F0",
    padding: 3,
    fontSize: 7,
    fontWeight: "bold",
  },
  tableBodyRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: "#AAAAAA",
    padding: 3,
  },
  totalRow: {
    fontWeight: "bold",
    backgroundColor: "#F0F0F0",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: BORDER_COLOR,
  },
  // Column Widths for Output VAT Table (5 columns)
  outputColNo: { width: "4%" },
  outputColCategory: { width: "40%" },
  outputColAmount: { width: "19%", textAlign: "right" },
  outputColVat: { width: "19%", textAlign: "right" },
  outputColAdjustment: { width: "18%", textAlign: "right" },

  // Column Widths for Input VAT Table (4 columns)
  inputColNo: { width: "4%" },
  inputColCategory: { width: "56%" },
  inputColAmount: { width: "20%", textAlign: "right" },
  inputColVat: { width: "20%", textAlign: "right" },

  // --- Summary (Net VAT Due) Styles ---
  summaryCellNo: { width: "5%", paddingLeft: 5 },
  summaryCellLabel: { width: "65%" },
  summaryCellValue: { width: "30%", textAlign: "right", paddingRight: 5 },
  summaryRowBold: { fontWeight: "bold" },

  // --- Declaration & Signatory Styles ---
  signatoryRow: {
    flexDirection: "row",
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderBottomWidth: 0.5,
    borderColor: "#CCC",
  },
  signatoryLabel: { width: "45%" },
  signatorySeparator: { width: "5%", textAlign: "center" },
  signatoryInputLine: {
    width: "50%",
  },
  finalDeclaration: {
    flexDirection: "row",
    alignItems: "center",
    padding: 5,
  },
  squareBox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#000000",
    justifyContent: "center", // Center content vertically
    alignItems: "center", // Center content horizontally
  },
});

// --- 2. Helper Components (Simplified) ---

const PdfHeaderSection = ({ title, style = {} }) => (
  <View style={[styles.barHeader, style]}>
    <Text>{title}</Text>
  </View>
);

const PdfInfoRow = ({ label, value }) => (
  <View style={styles.infoRow}>
    <Text style={styles.infoLabel}>{label}</Text>
    <Text style={styles.infoSeparator}>:</Text>
    <Text style={styles.infoValue}>{value}</Text>
  </View>
);

const PdfSignatoryRow = ({ label, value }) => (
  <View style={styles.signatoryRow}>
    <Text style={styles.signatoryLabel}>{label}</Text>
    <Text style={styles.signatorySeparator}>:</Text>
    <Text style={styles.signatoryInputLine}>{value}</Text>
  </View>
);

// --- 3. Main Converted Component ---

const VatReturnsFormPDF = ({ data, isSaudiPhase1 }) => {
  if (!data)
    return (
      <Document>
        <Page size="A4">
          <Text>Loading Data...</Text>
        </Page>
      </Document>
    );

  const { outputVat, inputVat, netVatDue } = data;
  const currency = isSaudiPhase1 ? "(SAR)" : "(AED)";

  const renderPdfTable = (headers, rows, isOutput) => {
    const colNoStyle = isOutput ? styles.outputColNo : styles.inputColNo;
    const colCategoryStyle = isOutput
      ? styles.outputColCategory
      : styles.inputColCategory;
    const colAmountStyle = isOutput
      ? styles.outputColAmount
      : styles.inputColAmount;
    const colVatStyle = isOutput ? styles.outputColVat : styles.inputColVat;

    return (
      <View style={styles.table}>
        {/* Table Header */}
        <View style={styles.tableHeaderRow}>
          <Text style={colNoStyle}>{headers[0]}</Text>
          <Text style={colCategoryStyle}>{headers[1]}</Text>
          <Text style={colAmountStyle}>{headers[2]}</Text>
          <Text style={colVatStyle}>{headers[3]}</Text>
          {isOutput && !isSaudiPhase1 && (
            <Text style={styles.outputColAdjustment}>{headers[4]}</Text>
          )}
          {isOutput && isSaudiPhase1 && (
            <Text style={styles.outputColAdjustment}>{headers[4]}</Text>
          )}
        </View>

        {/* Table Body */}
        {rows.map((row) => (
          <View
            key={row.no}
            style={[
              styles.tableBodyRow,
              (row.no === "8" || row.no === "11") && styles.totalRow,
            ]}
          >
            <Text style={colNoStyle}>{row.no}</Text>
            <Text style={colCategoryStyle}>{row.category}</Text>
            <Text style={colAmountStyle}>{row.amount}</Text>
            <Text style={colVatStyle}>{row.vatAmount}</Text>
            {isOutput && (
              <Text style={styles.outputColAdjustment}>{row.adjustment}</Text>
            )}
          </View>
        ))}
      </View>
    );
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* VAT RETURNS + General Info Container */}
        <View style={styles.sectionContainer}>
          {/* 1. VAT RETURNS Header */}
          <Text style={styles.mainHeader}>
            {isSaudiPhase1 ? "Saudi Arabia\nVAT Return Form" : "VAT RETURNS"}
          </Text>

          {/* 2. TRN / Company Info */}
          <View style={{ marginBottom: 5 }}>
            <PdfInfoRow
              label={isSaudiPhase1 ? "VAT REGISTRATION NUMBER" : "TRN"}
              value={""}
            />
            <PdfInfoRow label="TAXABLE COMPANY NAME" value={""} />
            <PdfInfoRow label="COMPANY ADDRESS" value={""} />
          </View>
        </View>

        {/* VAT RETURNS PERIOD Container */}
        <View style={styles.sectionContainer}>
          {/* 3. VAT RETURNS PERIOD Section */}
          <PdfHeaderSection title="VAT RETURNS PERIOD" />
          <View style={{ paddingVertical: 5 }}>
            <PdfInfoRow label="VAT RETURN PERIOD" value={""} />
            <PdfInfoRow label="VAT RETURN DUE DATE" value={""} />
            {isSaudiPhase1 ? null : (
              <PdfInfoRow label="TAX YEAR END" value={""} />
            )}
          </View>
        </View>

        {/* 4. VAT ON SALES AND ALL OTHER OUTPUTS Table */}
        <PdfHeaderSection title={isSaudiPhase1 ? "VAT on Sales" : "VAT ON SALES AND ALL OTHER OUTPUTS"} />
        {renderPdfTable(
          [
            "No",
            "Category",
            `Amount ${currency}`,
            `Vat Amount ${currency}`,
            `Adjustment ${currency}`,
          ],
          outputVat,
          true,
        )}

        {/* 5. VAT on Expenses and All Other Inputs Table */}
        <PdfHeaderSection title={isSaudiPhase1 ? "VAT on Purchases" : "VAT ON EXPENSES AND ALL OTHER INPUTS"} />
        {renderPdfTable(
          ["No", "Category", `Amount ${currency}`, `Vat Amount ${currency}`],
          inputVat,
          false,
        )}

        {/* 6. Net VAT Due / Summary Section */}
        <PdfHeaderSection title={isSaudiPhase1 ? "Summary" : "NET VAT DUE"} />
        <View style={styles.tableContainer}>
          {isSaudiPhase1 ? (
            <>
              <View style={styles.tableBodyRow}>
                <Text style={styles.summaryCellNo}>13</Text>
                <Text style={styles.summaryCellLabel}>Total VAT due for current period</Text>
                <Text style={styles.summaryCellValue}>{netVatDue.totalDueTax}</Text>
              </View>
              <View style={styles.tableBodyRow}>
                <Text style={styles.summaryCellNo}>14</Text>
                <Text style={styles.summaryCellLabel}>Corrections from previous preiod</Text>
                <Text style={styles.summaryCellValue}>{"0.00"}</Text>
              </View>
              <View style={styles.tableBodyRow}>
                <Text style={styles.summaryCellNo}>15</Text>
                <Text style={styles.summaryCellLabel}>VAT credit carried forward from previous period(s)</Text>
                <Text style={styles.summaryCellValue}>{"0.00"}</Text>
              </View>
              <View style={[styles.tableBodyRow, styles.totalRow, { borderBottomWidth: 0, paddingBottom: 10 }]}>
                <Text style={styles.summaryCellNo}></Text>
                <Text style={styles.summaryCellLabel}>Net VAT due (or claim)</Text>
                <Text style={styles.summaryCellValue}>{netVatDue.payableTax}</Text>
              </View>
            </>
          ) : (
            <>
              <View style={styles.tableBodyRow}>
                <Text style={styles.summaryCellNo}>12</Text>
                <Text style={styles.summaryCellLabel}>
                  Total value of due tax for the period
                </Text>
                <Text style={styles.summaryCellValue}>{netVatDue.totalDueTax}</Text>
              </View>
              <View style={styles.tableBodyRow}>
                <Text style={styles.summaryCellNo}>13</Text>
                <Text style={styles.summaryCellLabel}>
                  Total value of recoverable tax for the period
                </Text>
                <Text style={styles.summaryCellValue}>
                  {netVatDue.totalRecoverableTax}
                </Text>
              </View>
              <View style={[styles.tableBodyRow, styles.totalRow]}>
                <Text style={styles.summaryCellNo}>14</Text>
                <Text style={styles.summaryCellLabel}>
                  Payable tax for the period
                </Text>
                <Text style={styles.summaryCellValue}>{netVatDue.payableTax}</Text>
              </View>
              <View
                style={[
                  styles.tableBodyRow,
                  { borderBottomWidth: 0, paddingBottom: 10 },
                ]}
              >
                <Text style={styles.summaryCellNo}>15</Text>
                <Text style={styles.summaryCellLabel}>
                  {netVatDue.excessRefundRequest}
                </Text>
                <Text style={styles.summaryCellValue}> </Text>
              </View>
            </>
          )}
        </View>

        {!isSaudiPhase1 && (
          <>
            {/* 7. Additional Reporting Requirements */}
            <PdfHeaderSection title="ADDITIONAL REPORTING REQUIREMENTS" />
            <View style={styles.sectionContainer}>
              <PdfHeaderSection
                title="Profit Margin Scheme"
                style={{ borderTopWidth: 0, borderBottomWidth: 0 }}
              />
              <View style={{ padding: 8 }}>
                <Text>
                  Did you apply the profit margin schemein respect of any supplies
                  made during the tax period ?{" "}
                </Text>
                <View
                  style={{ flexDirection: "row", marginTop: 5, marginLeft: 20 }}
                >
                  <View style={styles.squareBox}></View>
                  <Text style={{ marginRight: 20 }}> YES </Text>
                  <View style={styles.squareBox}></View>
                  <Text> NO</Text>
                </View>
              </View>
            </View>
          </>
        )}

        {/* 8. Declaration and Authorised Signatory */}
        <PdfHeaderSection title="DECLARATION AND AUTHORISED SIGNATORY" />
        <View style={styles.tableContainer}>
          <PdfSignatoryRow label="NAME In English" value={""} />
          <PdfSignatoryRow label="PHONE/ MOBILE Country Code" value={""} />
          <PdfSignatoryRow label="PHONE/ MOBILE Number" value={""} />
          <PdfSignatoryRow
            label={`DATE of Submission ${"("}dd/mm/yyyy${")"}`}
            value={""}
          />
          <PdfSignatoryRow label="E-mail Address" value={""} />
        </View>

        {/* 9. Final Declaration */}
        <View style={styles.finalDeclaration}>
          <View style={styles.squareBox}></View>
          <Text style={{ flexGrow: 1 }}>
            {" "}
            I declare all the information provided is true, accurate and
            complete to the best of my knowledge and belief
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default VatReturnsFormPDF;
